// =============================================================================
// LOGIN FORM - CON SOPORTE 2FA (CORREGIDO)
// =============================================================================
// Ubicación: src/components/LoginForm.tsx
// REEMPLAZAR tu LoginForm.tsx existente con este código
// =============================================================================

import React, { useState } from 'react';
import { Button, Form, Spin } from 'antd';
import { message } from '../services/toastMessage';
import { useTranslation } from 'react-i18next';
import { loginUser } from '../services/AuthenticationService';
import { ILoginForm } from '../interfaces/forms/ILoginForm';
import { useAuth } from '../contextProviders/AuthProvider';
import NotAuthenticatedLayout from './Layouts/NotAuthenticatedLayout';
import ConfirmationPendingNotice from './ConfirmationPendingNotice';
import OAuthButtons from './OAuth/OAuthButtons';
import Login2FAForm from './TwoFactor/Login2FAForm';  // ← NUEVO IMPORT
import FloatingLabelInput from './FloatingLabelInput';
import { translate } from '../helpers/translationHelper';
import { TRANSLATIONS } from '../constants/translations';
import { TEAL } from '../theme/creamyTokens';
import { useAppBreakpoint } from '../hooks/useAppBreakpoint';

const LoginForm: React.FC = () => {
  const { login } = useAuth();
  const { t } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  const [loading, setLoading] = useState(false);

  // ✅ NUEVO: Estado para 2FA
  const [requires2FA, setRequires2FA] = useState(false);
  const [userId2FA, setUserId2FA] = useState('');
  const [email2FA, setEmail2FA] = useState('');

  // Set when the backend rejects a correct password with EMAIL_NOT_CONFIRMED
  // (see AuthController.Login) -- swaps the form for the same "check your
  // email" card RegistrationForm shows, with its resend button, instead of
  // a plain toast that leaves the person stuck with no next step.
  const [unconfirmedEmail, setUnconfirmedEmail] = useState<string | null>(null);

  const onFinish = async (values: ILoginForm) => {
    setLoading(true);
    try {
      const response = await loginUser(values.email, values.password);
      
      // ✅ CORREGIDO: Verificar si requiere 2FA con validación
      if (response.requiresTwoFactor) {
        // ✅ Validación de seguridad - el servidor DEBE enviar userId
        if (!response.userId) {
          message.error(translate(t, TRANSLATIONS.LOGIN_FORM_INVALID_SERVER_RESPONSE));
          return;
        }

        // Mostrar formulario de 2FA
        setRequires2FA(true);
        setUserId2FA(response.userId);  // ✅ Ahora TypeScript sabe que no es undefined
        setEmail2FA(response.email ?? values.email);  // ✅ Fallback al email ingresado
        message.info(translate(t, TRANSLATIONS.LOGIN_FORM_2FA_PROMPT_INFO));
      } else {
        // Login normal sin 2FA
        login(response.token, response.refreshToken);
      }
      
    } catch (error: any) {
      console.error('Login error:', error);

      if (error.message === 'EMAIL_NOT_CONFIRMED') {
        setUnconfirmedEmail(values.email);
        return;
      }

      const statusCode = error.response?.status;
      const errorData = error.response?.data?.data;
      const errorMessage = error.message || translate(t, TRANSLATIONS.LOGIN_FORM_ERROR_FALLBACK);

      // ... (resto del manejo de errores - mantener igual) ...

      if (statusCode === 401 && typeof errorData === 'object' && errorData?.deletionDate) {
        const deletionDate = new Date(errorData.deletionDate).toLocaleDateString();
        const daysRemaining = errorData.daysRemaining || 'unknown';
        message.error({
          content: (
            <div>
              <strong>{translate(t, TRANSLATIONS.LOGIN_FORM_ACCOUNT_DELETION_TITLE)}</strong>
              <p>{t(TRANSLATIONS.LOGIN_FORM_ACCOUNT_DELETION_BODY, { date: deletionDate })}</p>
              <p>{t(TRANSLATIONS.LOGIN_FORM_ACCOUNT_DELETION_DAYS_REMAINING, { days: daysRemaining })}</p>
              <p style={{ fontSize: '12px', marginTop: '8px' }}>
                {translate(t, TRANSLATIONS.LOGIN_FORM_ACCOUNT_DELETION_CHECK_EMAIL)}
              </p>
            </div>
          ),
          duration: 10,
        });
      }
      else if (statusCode === 403) {
        const remainingMinutes = errorData?.remainingMinutes
          ? Math.ceil(errorData.remainingMinutes)
          : 'unknown';
        message.error({
          content: (
            <div>
              <strong>{translate(t, TRANSLATIONS.LOGIN_FORM_ACCOUNT_LOCKED_TITLE)}</strong>
              <p>{errorData?.message || translate(t, TRANSLATIONS.LOGIN_FORM_ACCOUNT_LOCKED_DEFAULT_MESSAGE)}</p>
              <p>{t(TRANSLATIONS.LOGIN_FORM_ACCOUNT_LOCKED_UNLOCK_IN, { minutes: remainingMinutes })}</p>
              <p style={{ fontSize: '12px', marginTop: '8px' }}>
                {translate(t, TRANSLATIONS.LOGIN_FORM_ACCOUNT_LOCKED_CONTACT_SUPPORT)}
              </p>
            </div>
          ),
          duration: 10,
        });
      }
      else {
        message.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // ✅ NUEVO: Handler para volver del formulario 2FA
  const handleBackFrom2FA = () => {
    setRequires2FA(false);
    setUserId2FA('');
    setEmail2FA('');
  };

  if (unconfirmedEmail) {
    return (
      <NotAuthenticatedLayout>
        <ConfirmationPendingNotice
          email={unconfirmedEmail}
          title={translate(t, TRANSLATIONS.LOGIN_EMAIL_NOT_CONFIRMED_TITLE)}
          description={translate(t, TRANSLATIONS.LOGIN_EMAIL_NOT_CONFIRMED_DESCRIPTION)}
        />
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Button type="link" onClick={() => setUnconfirmedEmail(null)} style={{ color: TEAL, fontWeight: 500 }}>
            {translate(t, TRANSLATIONS.LOGIN_EMAIL_NOT_CONFIRMED_BACK)}
          </Button>
        </div>
      </NotAuthenticatedLayout>
    );
  }

  // ✅ NUEVO: Mostrar Login2FAForm si se requiere 2FA
  if (requires2FA) {
    return (
      <NotAuthenticatedLayout>
        <Login2FAForm
          userId={userId2FA}
          email={email2FA}
          onBack={handleBackFrom2FA}
        />
      </NotAuthenticatedLayout>
    );
  }

  // ✅ Formulario de login normal
  return (
    <NotAuthenticatedLayout>
      <div style={{
        maxWidth: 400,
        margin: isMobile ? '12px auto' : '50px auto',
        width: '100%',
        padding: isMobile ? 0 : '0 16px',
      }}>
        <h1 style={{ marginBottom: 24 }}>{translate(t, TRANSLATIONS.LOGIN_TITLE)}</h1>

        <Spin spinning={loading}>
          <Form layout="vertical" onFinish={onFinish}>
            <Form.Item
              name="email"
              rules={[{ required: true, message: translate(t, TRANSLATIONS.EMAIL_MESSAGE) }]}
            >
              <FloatingLabelInput label={translate(t, TRANSLATIONS.EMAIL)} disabled={loading} style={{ borderRadius: 999 }} />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[{ required: true, message: translate(t, TRANSLATIONS.PASSWORD_MESSAGE) }]}
            >
              <FloatingLabelInput label={translate(t, TRANSLATIONS.PASSWORD)} password disabled={loading} style={{ borderRadius: 999 }} />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                block
                disabled={loading}
                style={{ borderRadius: 999, height: 48 }}
              >
                {loading ? translate(t, TRANSLATIONS.LOGGING_IN) : translate(t, TRANSLATIONS.LOG_IN)}
              </Button>
            </Form.Item>
          </Form>

          <OAuthButtons showDivider={true} iconOnly />

        </Spin>

        <div style={{ marginTop: 20, textAlign: 'center', fontSize: 14 }}>
          <div>
            <a href="/forgot" style={{ color: TEAL, fontWeight: 500 }}>
              {translate(t, TRANSLATIONS.FORGOT_PASSWORD)}
            </a>
          </div>
          <div style={{ marginTop: 4 }}>
            <span style={{ color: 'rgba(0, 0, 0, 0.45)' }}>{translate(t, TRANSLATIONS.NO_ACCOUNT)} </span>
            <a href="/register" style={{ color: TEAL, fontWeight: 500 }}>
              {translate(t, TRANSLATIONS.REGISTER_HERE)}
            </a>
          </div>
        </div>
      </div>
    </NotAuthenticatedLayout>
  );
};

export default LoginForm;